import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, X } from "lucide-react";
import swal from "sweetalert";
import {
  createPromoPopup,
  fetchCouponOptions,
  getPromoPopupDetail,
  updatePromoPopup,
} from "../../../redux/promoPopups/action";
import { setPromoPopupDetail } from "../../../reusableSlice";
import { uploadImageToS3 } from "../../../utils/s3Upload";
import PromoPopupPreview from "./PromoPopupPreview";
import { buildPayload, toBoundedInt, validate } from "./popupPayload";
import {
  ALLOWED_IMAGE_ACCEPT,
  ALLOWED_IMAGE_TYPES,
  AUDIENCE_OPTIONS,
  MAX_DELAY_SECONDS,
  MAX_FREQUENCY_DAYS,
  S3_POPUPS_FOLDER,
  SURFACE_OPTIONS,
} from "./constants";
import "../DynamicSections/HomePageCRM.css";
import "./PromoPopups.css";

const EMPTY_FORM = {
  name: "",
  audience: "guests",
  surface: "both",
  // Off on create. Going live is a decision, and a campaign that publishes
  // itself the moment it is saved is one nobody proofread. The backend
  // defaults the same way.
  is_active: false,
  delay_seconds: 5,
  frequency_days: 7,
  offer_id: "",
  image: "",
  ar_image: "",
  headline: "",
  ar_headline: "",
  body: "",
  ar_body: "",
  cta_label: "",
  ar_cta_label: "",
  cta_url: "",
  ar_cta_url: "",
  starts_at: "",
  ends_at: "",
};

const toDateTimeLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");

const ImageUploadSlot = ({ id, label, hint, value, uploading, onChange, onClear }) => {
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      await onChange(file);
    } catch (err) {
      swal({ text: err.message || "Upload failed", icon: "error", timer: 1800 });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="ds-form-group">
      <label>{label}</label>
      {value ? (
        <div className="pp-image-preview">
          <img src={value} alt={label} />
          <button
            type="button"
            className="pp-image-preview__remove"
            onClick={onClear}
            aria-label={`Remove ${label}`}
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <label htmlFor={id} className="pp-upload-area">
          {uploading ? <span>Uploading...</span> : (
            <>
              <Upload size={16} />
              <span>Click to upload</span>
            </>
          )}
        </label>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={ALLOWED_IMAGE_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {hint ? <span className="pp-help-text">{hint}</span> : null}
    </div>
  );
};

const PromoPopupForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const detail = useSelector((state) => state.reusableSlice?.promoPopupDetail);

  const [form, setForm] = useState(EMPTY_FORM);
  const [coupons, setCoupons] = useState([]);
  const [couponsLoaded, setCouponsLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState("en");
  const [previewDevice, setPreviewDevice] = useState("desktop");

  // Track which id has been hydrated. Re-hydrate when the id changes (edit/1 →
  // edit/2 with the form already mounted) but never twice for the same id —
  // that would throw away edits in progress.
  const lastHydratedId = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchCouponOptions().then((options) => {
      if (cancelled) return;
      setCoupons(options);
      setCouponsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    setPageLoading(lastHydratedId.current !== id);
    dispatch(getPromoPopupDetail(id));
    return () => dispatch(setPromoPopupDetail(null));
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (!isEdit || !detail) return;
    // Guard against detail still in redux from the previously viewed id.
    if (String(detail.id) !== String(id)) return;
    if (lastHydratedId.current === id) return;
    lastHydratedId.current = id;
    setForm({
      name: detail.name || "",
      audience: detail.audience || "guests",
      surface: detail.surface || "both",
      is_active: detail.is_active ?? false,
      delay_seconds: detail.delay_seconds ?? 5,
      frequency_days: detail.frequency_days ?? 7,
      offer_id: detail.offer_id ? String(detail.offer_id) : "",
      image: detail.image || "",
      ar_image: detail.ar_image || "",
      headline: detail.headline || "",
      ar_headline: detail.ar_headline || "",
      body: detail.body || "",
      ar_body: detail.ar_body || "",
      cta_label: detail.cta_label || "",
      ar_cta_label: detail.ar_cta_label || "",
      cta_url: detail.cta_url || "",
      ar_cta_url: detail.ar_cta_url || "",
      starts_at: toDateTimeLocal(detail.starts_at),
      ends_at: toDateTimeLocal(detail.ends_at),
    });
    setPageLoading(false);
  }, [detail, isEdit, id]);

  const setField = useCallback(
    (key, value) => setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const uploadImage = async (field, file) => {
    setUploadingField(field);
    try {
      const { file_url } = await uploadImageToS3(file, {
        folder: S3_POPUPS_FOLDER,
        allowedTypes: ALLOWED_IMAGE_TYPES,
      });
      setField(field, file_url);
    } finally {
      setUploadingField(null);
    }
  };

  const selectedCoupon = useMemo(
    () => coupons.find((c) => String(c.id) === String(form.offer_id)) || null,
    [coupons, form.offer_id],
  );

  // An edited popup can point at a coupon the picker will not list — one that
  // has since been deactivated, or that the offers list no longer returns.
  // Saying so beats silently resetting the field to "No coupon", which is how
  // a live campaign quietly loses its code on an unrelated edit.
  const orphanedCouponId =
    couponsLoaded && form.offer_id && !selectedCoupon ? form.offer_id : null;

  const audienceHint = useMemo(
    () => AUDIENCE_OPTIONS.find((o) => o.value === form.audience)?.hint || "",
    [form.audience],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(form);
    if (errors.length > 0) {
      swal({ text: errors[0], icon: "warning", timer: 2400 });
      return;
    }
    const payload = buildPayload(form);
    const onSuccess = () => navigate("/promo-popups");
    if (isEdit) {
      dispatch(updatePromoPopup({ id: Number(id), ...payload }, setSubmitting, onSuccess));
    } else {
      dispatch(createPromoPopup(payload, setSubmitting, onSuccess));
    }
  };

  if (pageLoading) {
    return (
      <div className="ds-container">
        <div className="ds-empty-state" style={{ height: 200 }}>Loading popup...</div>
      </div>
    );
  }

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            className="ds-btn-secondary"
            onClick={() => navigate("/promo-popups")}
            style={{ padding: "6px 10px" }}
            aria-label="Back to popups"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h4>{isEdit ? "Edit Popup" : "Create Popup"}</h4>
            <p>
              {isEdit
                ? "Update the offer, timing, and artwork for this campaign."
                : "A message that appears a few seconds after someone lands, and closes."}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pp-layout">
          <div className="ds-form-container pp-layout__form">

            {/* ── Who sees it, and when ── */}
            <div className="ds-form-section">
              <h5>Campaign</h5>
              <div className="ds-form-grid">
                <div className="ds-form-group">
                  <label>Campaign name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="e.g. Ramadan First Order"
                    maxLength={255}
                  />
                  <span className="pp-help-text">
                    Never shown to customers — this is what the campaign is
                    called in analytics and in this list. Keep it in English so
                    one campaign stays one row in reports.
                  </span>
                </div>

                <div className="ds-form-group">
                  <label>Who sees it</label>
                  <select
                    value={form.audience}
                    onChange={(e) => setField("audience", e.target.value)}
                  >
                    {AUDIENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="pp-help-text">{audienceHint}</span>
                </div>

                <div className="ds-form-group">
                  <label>Where it runs</label>
                  <select
                    value={form.surface}
                    onChange={(e) => setField("surface", e.target.value)}
                  >
                    {SURFACE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="pp-help-text">
                    An app-install campaign belongs on Web only — inside the app
                    it would invite someone to install what they are using.
                  </span>
                </div>

                <div className="ds-form-group">
                  <label>Live</label>
                  <div className="ds-toggle-row">
                    <label className="ds-toggle">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => setField("is_active", e.target.checked)}
                      />
                      <span className="ds-toggle-slider" />
                    </label>
                    <span style={{ fontSize: 13 }}>
                      {form.is_active ? "Showing to customers" : "Not showing"}
                    </span>
                  </div>
                  <span className="pp-help-text">
                    New campaigns start switched off. Turn this on when the
                    preview looks right.
                  </span>
                </div>
              </div>
            </div>

            {/* ── Timing ── */}
            <div className="ds-form-section">
              <h5>Timing</h5>
              <div className="ds-form-grid">
                <div className="ds-form-group">
                  <label>Appears after (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    max={MAX_DELAY_SECONDS}
                    value={form.delay_seconds}
                    onChange={(e) => setField("delay_seconds", e.target.value)}
                    onBlur={(e) =>
                      setField("delay_seconds", toBoundedInt(e.target.value, 5, MAX_DELAY_SECONDS))
                    }
                  />
                  <span className="pp-help-text">
                    Long enough that the page has settled. Around 5 seconds is
                    usual; 0 fires during the first paint.
                  </span>
                </div>

                <div className="ds-form-group">
                  <label>Stays hidden for (days)</label>
                  <input
                    type="number"
                    min={0}
                    max={MAX_FREQUENCY_DAYS}
                    value={form.frequency_days}
                    onChange={(e) => setField("frequency_days", e.target.value)}
                    onBlur={(e) =>
                      setField("frequency_days", toBoundedInt(e.target.value, 7, MAX_FREQUENCY_DAYS))
                    }
                  />
                  <span className="pp-help-text">
                    After someone closes it. 0 means it comes back on their next
                    visit.
                  </span>
                </div>

                <div className="ds-form-group">
                  <label>Starts</label>
                  <input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setField("starts_at", e.target.value)}
                  />
                  <span className="pp-help-text">Riyadh time. Leave blank to start immediately.</span>
                </div>

                <div className="ds-form-group">
                  <label>Ends</label>
                  <input
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setField("ends_at", e.target.value)}
                  />
                  <span className="pp-help-text">Leave blank to run until switched off.</span>
                </div>
              </div>
            </div>

            {/* ── Coupon ── */}
            <div className="ds-form-section">
              <h5>Coupon</h5>
              <div className="ds-form-group">
                <label>Advertise a coupon</label>
                <select
                  value={form.offer_id}
                  onChange={(e) => setField("offer_id", e.target.value)}
                >
                  <option value="">No coupon — announcement or app install</option>
                  {orphanedCouponId ? (
                    <option value={orphanedCouponId}>
                      Coupon #{orphanedCouponId} — no longer available
                    </option>
                  ) : null}
                  {coupons.map((coupon) => (
                    <option key={coupon.id} value={coupon.id}>
                      {coupon.coupon_code} — {coupon.name}
                    </option>
                  ))}
                </select>
                {orphanedCouponId ? (
                  <span className="pp-help-text pp-help-text--warn">
                    This campaign points at a coupon that is no longer active,
                    so it is not being shown to anyone. Pick a live coupon, or
                    choose “No coupon”.
                  </span>
                ) : (
                  <span className="pp-help-text">
                    The code is read from the coupon itself, so it can never
                    drift. If the coupon expires or runs out, this popup stops
                    appearing on its own — better nothing than a code the cart
                    refuses.
                    {couponsLoaded && coupons.length === 0
                      ? " There are no active coupons to choose from yet."
                      : ""}
                  </span>
                )}
                {selectedCoupon ? (
                  <div className="pp-coupon-facts">
                    <span><strong>{selectedCoupon.coupon_code}</strong></span>
                    <span>
                      {Number(selectedCoupon.discount_type) === 1
                        ? `${selectedCoupon.discount_value}% off`
                        : `${selectedCoupon.discount_value} SAR off`}
                    </span>
                    {selectedCoupon.min_order_value ? (
                      <span>Min order {selectedCoupon.min_order_value} SAR</span>
                    ) : null}
                    <span>Ends {new Date(selectedCoupon.end_date).toLocaleDateString("en-GB")}</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* ── Creative ── */}
            <div className="ds-form-section">
              <h5>Artwork</h5>
              <div className="ds-form-grid">
                <ImageUploadSlot
                  id="pp-image"
                  label="Image (default)"
                  hint="Used for English, and for Arabic when no Arabic image is set."
                  value={form.image}
                  uploading={uploadingField === "image"}
                  onChange={(file) => uploadImage("image", file)}
                  onClear={() => setField("image", "")}
                />
                <ImageUploadSlot
                  id="pp-ar-image"
                  label="Image (Arabic)"
                  hint="Only needed when the artwork carries Arabic words."
                  value={form.ar_image}
                  uploading={uploadingField === "ar_image"}
                  onChange={(file) => uploadImage("ar_image", file)}
                  onClear={() => setField("ar_image", "")}
                />
              </div>
            </div>

            <div className="ds-form-section">
              <h5>Words</h5>
              <div className="ds-form-grid">
                <div className="ds-form-group">
                  <label>Headline (English)</label>
                  <input
                    type="text"
                    value={form.headline}
                    onChange={(e) => setField("headline", e.target.value)}
                    placeholder="10% off your first order"
                    maxLength={255}
                  />
                </div>
                <div className="ds-form-group">
                  <label>Headline (Arabic)</label>
                  <input
                    type="text"
                    dir="rtl"
                    value={form.ar_headline}
                    onChange={(e) => setField("ar_headline", e.target.value)}
                    placeholder="خصم ١٠٪ على أول طلب"
                    maxLength={255}
                  />
                </div>
                <div className="ds-form-group">
                  <label>Supporting line (English)</label>
                  <textarea
                    rows={2}
                    value={form.body}
                    onChange={(e) => setField("body", e.target.value)}
                    placeholder="On orders over 150 SAR"
                  />
                </div>
                <div className="ds-form-group">
                  <label>Supporting line (Arabic)</label>
                  <textarea
                    rows={2}
                    dir="rtl"
                    value={form.ar_body}
                    onChange={(e) => setField("ar_body", e.target.value)}
                    placeholder="على الطلبات فوق ١٥٠ ريال"
                  />
                </div>
              </div>
            </div>

            {/* ── Button ── */}
            <div className="ds-form-section">
              <h5>Button</h5>
              <div className="ds-form-grid">
                <div className="ds-form-group">
                  <label>Button text (English)</label>
                  <input
                    type="text"
                    value={form.cta_label}
                    onChange={(e) => setField("cta_label", e.target.value)}
                    placeholder="Shop now"
                    maxLength={100}
                  />
                </div>
                <div className="ds-form-group">
                  <label>Button text (Arabic)</label>
                  <input
                    type="text"
                    dir="rtl"
                    value={form.ar_cta_label}
                    onChange={(e) => setField("ar_cta_label", e.target.value)}
                    placeholder="تسوّق الآن"
                    maxLength={100}
                  />
                </div>
                <div className="ds-form-group">
                  <label>Button link</label>
                  <input
                    type="text"
                    value={form.cta_url}
                    onChange={(e) => setField("cta_url", e.target.value)}
                    placeholder="/productlist?brands=dior"
                  />
                  <span className="pp-help-text">
                    A storefront path like <code>/productlist?brands=dior</code>,
                    or a full link. For an app campaign paste either store link —
                    the site sends iPhone visitors to the App Store and Android
                    visitors to Google Play whichever one you use, so nobody
                    lands on a listing they cannot install from.
                  </span>
                </div>
                <div className="ds-form-group">
                  <label>Button link (Arabic)</label>
                  <input
                    type="text"
                    value={form.ar_cta_url}
                    onChange={(e) => setField("ar_cta_url", e.target.value)}
                    placeholder="Only if Arabic goes somewhere different"
                  />
                </div>
              </div>
              <span className="pp-help-text">
                A button needs both text and a link, or neither. Leave both
                blank for a popup that only announces something.
              </span>
            </div>

            <div className="ds-form-actions">
              <button
                type="button"
                className="ds-btn-secondary"
                onClick={() => navigate("/promo-popups")}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="ds-btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : isEdit ? "Save changes" : "Create popup"}
              </button>
            </div>
          </div>

          <aside className="pp-layout__preview">
            <PromoPopupPreview
              form={form}
              coupon={selectedCoupon}
              language={previewLanguage}
              device={previewDevice}
              onLanguageChange={setPreviewLanguage}
              onDeviceChange={setPreviewDevice}
            />
          </aside>
        </div>
      </form>
    </div>
  );
};

export default PromoPopupForm;
